import { supabase } from '../config/supabase';

/**
 * Moderation Service
 * Handles user reporting and admin moderation actions
 */

export type ReportReason = 'inappropriate' | 'scam' | 'copyright' | 'other';
export type ModerationAction = 'approved' | 'deleted' | 'flagged' | 'banned_user';

export interface ItemReport {
  id: string;
  reporter_id: string;
  item_id: string;
  reason: ReportReason;
  details: string | null;
  created_at: string;
}

export interface ModerationLog {
  id: string;
  moderator_id: string | null;
  item_id: string | null;
  action: ModerationAction;
  reason: string | null;
  created_at: string;
}

export interface ReportedItem {
  id: string;
  title: string;
  description: string;
  price: number;
  image_urls: string[] | null;
  report_count: number;
  seller: {
    id: string;
    name: string;
    email: string;
  };
  reports: ItemReport[];
}

/**
 * Report an item for inappropriate content
 */
export const reportItem = async (
  itemId: string,
  reason: ReportReason,
  details?: string
): Promise<void> => {
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) {
    throw new Error('Must be authenticated to report items');
  }

  const { error } = await supabase
    .from('item_reports')
    .insert({
      reporter_id: user.id,
      item_id: itemId,
      reason,
      details: details || null
    });

  if (error) {
    if (error.code === '23505') { // Unique constraint violation
      throw new Error('You have already reported this item');
    }
    throw error;
  }
};

/**
 * Get all reported items (admin only)
 */
export const getReportedItems = async (): Promise<ReportedItem[]> => {
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) {
    throw new Error('Must be authenticated');
  }

  // Check if user is admin
  const { data: profile } = await supabase
    .from('profiles')
    .select('is_platform_admin')
    .eq('id', user.id)
    .single();

  if (!profile?.is_platform_admin) {
    throw new Error('Admin access required');
  }

  const { data, error } = await supabase
    .from('clothing_items')
    .select(`
      id,
      title,
      description,
      price,
      image_urls,
      report_count,
      seller:profiles!clothing_items_seller_id_fkey (
        id,
        name,
        email
      ),
      reports:item_reports (
        id,
        reporter_id,
        item_id,
        reason,
        details,
        created_at
      )
    `)
    .gt('report_count', 0)
    .order('report_count', { ascending: false });

  if (error) throw error;

  return data as unknown as ReportedItem[];
};

/**
 * Approve an item (reset report count) - admin only
 */
export const approveItem = async (itemId: string, reason?: string): Promise<void> => {
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) {
    throw new Error('Must be authenticated');
  }

  // Check if user is admin
  const { data: profile } = await supabase
    .from('profiles')
    .select('is_platform_admin')
    .eq('id', user.id)
    .single();

  if (!profile?.is_platform_admin) {
    throw new Error('Admin access required');
  }

  // Reset report count
  const { error: resetError } = await supabase
    .rpc('reset_item_report_count', { item_id_param: itemId });

  if (resetError) throw resetError;

  // Log moderation action
  const { error: logError } = await supabase
    .from('moderation_logs')
    .insert({
      moderator_id: user.id,
      item_id: itemId,
      action: 'approved',
      reason: reason || null
    });

  if (logError) throw logError;
};

/**
 * Delete an item and log moderation action - admin only
 */
export const deleteReportedItem = async (itemId: string, reason?: string): Promise<void> => {
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) {
    throw new Error('Must be authenticated');
  }

  // Check if user is admin
  const { data: profile } = await supabase
    .from('profiles')
    .select('is_platform_admin')
    .eq('id', user.id)
    .single();

  if (!profile?.is_platform_admin) {
    throw new Error('Admin access required');
  }

  // Log moderation action before deletion
  const { error: logError } = await supabase
    .from('moderation_logs')
    .insert({
      moderator_id: user.id,
      item_id: itemId,
      action: 'deleted',
      reason: reason || null
    });

  if (logError) throw logError;

  // Delete the item (cascades to reports)
  const { error: deleteError } = await supabase
    .from('clothing_items')
    .delete()
    .eq('id', itemId);

  if (deleteError) throw deleteError;
};

/**
 * Check if current user is admin
 */
export const isCurrentUserAdmin = async (): Promise<boolean> => {
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) return false;

  const { data: profile } = await supabase
    .from('profiles')
    .select('is_platform_admin')
    .eq('id', user.id)
    .single();

  return profile?.is_platform_admin === true;
};

/**
 * Get moderation logs (admin only)
 */
export const getModerationLogs = async (limit = 50): Promise<ModerationLog[]> => {
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) {
    throw new Error('Must be authenticated');
  }

  // Check if user is admin
  const { data: profile } = await supabase
    .from('profiles')
    .select('is_platform_admin')
    .eq('id', user.id)
    .single();

  if (!profile?.is_platform_admin) {
    throw new Error('Admin access required');
  }

  const { data, error } = await supabase
    .from('moderation_logs')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) throw error;

  return data as ModerationLog[];
};

export const moderationService = {
  reportItem,
  getReportedItems,
  approveItem,
  deleteReportedItem,
  isCurrentUserAdmin,
  getModerationLogs
};
